/**
 * FileUpload Component
 *
 * Drag & drop multi-file upload with list + previews.
 * This component is UI-focused; the parent owns upload behavior/state.
 */

import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Loader2, Paperclip, Upload, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { resolveFileUrl } from '../../../utils/fileUrl';

export type UploadStatus = 'idle' | 'uploading' | 'uploaded' | 'error';

export interface UploadFileItem {
  id: string;
  name: string;
  size: number;
  type: string;
  status: UploadStatus;
  url?: string;
  error?: string;
}

export interface FileUploadProps {
  name?: string;
  label?: string;
  hint?: string;
  errorMessage?: string;
  files: UploadFileItem[];
  onAddFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  /** Horizontal drop row used on the requisition form. */
  appearance?: 'default' | 'banner';
  /** Shows a required asterisk on the banner drop line. */
  requiredMark?: boolean;
}

const fmtSize = (bytes: number) => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

const getExtension = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

const isPreviewableImage = (t: string) => t.startsWith('image/');
const isPreviewableVideo = (t: string) => t.startsWith('video/');
const isPdfFile = (f: UploadFileItem) =>
  f.type === 'application/pdf' || getExtension(f.name) === 'pdf';

const canViewFile = (f: UploadFileItem) => f.status === 'uploaded' && Boolean(f.url);

const opensInModal = (f: UploadFileItem) =>
  isPreviewableImage(f.type) || isPreviewableVideo(f.type) || isPdfFile(f);

export const FileUpload: React.FC<FileUploadProps> = ({
  name,
  label = 'Attachments',
  hint = 'Images, videos, PDFs and files are supported.',
  errorMessage,
  files,
  onAddFiles,
  onRemove,
  disabled = false,
  appearance = 'default',
  requiredMark = false,
}) => {
  const [previewFile, setPreviewFile] = useState<UploadFileItem | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;
      onAddFiles(acceptedFiles);
    },
    [onAddFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled,
    // allow all file types (backend may still validate)
  });

  const closePreview = () => setPreviewFile(null);

  const viewFile = (file: UploadFileItem) => {
    if (!canViewFile(file)) return;
    const resolved = resolveFileUrl(file.url) ?? file.url!;
    if (opensInModal(file)) {
      setPreviewFile({ ...file, url: resolved });
      return;
    }
    window.open(resolved, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-2" data-field={name}>
      {label && <div className="text-[11px] text-slate-400">{label}</div>}

      <div
        {...getRootProps()}
        className={
          appearance === 'banner'
            ? [
                'flex items-center gap-3.5 rounded-[10px] border-[1.5px] border-dashed px-5 py-[22px] text-left transition-colors cursor-pointer',
                disabled ? 'opacity-60 cursor-not-allowed' : '',
                isDragActive ? 'border-[#A8213A] bg-[#A8213A]/5' : 'border-[#D1D5DB] bg-[#F9FAFB] hover:border-[#A8213A]',
              ].join(' ')
            : [
                'rounded-xl border-2 border-dashed px-4 py-4 transition-colors cursor-pointer bg-white',
                disabled ? 'opacity-60 cursor-not-allowed' : '',
                isDragActive ? 'border-primary-500 bg-primary-500/5' : 'border-slate-200 hover:border-primary-400',
              ].join(' ')
        }
      >
        <input {...getInputProps({ name })} />
        {appearance === 'banner' ? (
          <>
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full border border-[#E5E7EB] bg-white">
              <Upload className="h-5 w-5 text-[#A8213A]" />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-base font-semibold text-[#1C1B1A]">
                {isDragActive ? 'Drop files here…' : (
                  <>Drop files or <span className="text-[#A8213A] underline">browse</span>{requiredMark && <span className="text-[#A8213A]"> *</span>}</>
                )}
              </span>
              <span className="text-sm text-[#6B6862]">{hint}</span>
            </span>
          </>
        ) : (
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 grid place-items-center">
            <Upload className="h-5 w-5 text-slate-600" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              {isDragActive ? 'Drop files here…' : 'Drag & drop files, or click to browse'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
          </div>
        </div>
        )}
      </div>

      {errorMessage && (
        <div role="alert" className="text-sm text-[#c00035]">
          {errorMessage}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {f.url && (isPreviewableImage(f.type) || isPreviewableVideo(f.type)) ? (
                    <button
                      type="button"
                      onClick={() => viewFile(f)}
                      className="h-12 w-12 rounded-lg border border-slate-100 overflow-hidden hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#c00035]/30 flex-shrink-0"
                      title="View file"
                    >
                      {isPreviewableImage(f.type) ? (
                        <img
                          src={resolveFileUrl(f.url) ?? f.url}
                          alt={f.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <video
                          src={resolveFileUrl(f.url) ?? f.url}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => viewFile(f)}
                      disabled={!canViewFile(f)}
                      className={[
                        'h-10 w-10 rounded-2xl bg-slate-50 border border-slate-200 grid place-items-center flex-shrink-0',
                        canViewFile(f)
                          ? 'cursor-pointer hover:border-slate-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#c00035]/30'
                          : 'cursor-default',
                      ].join(' ')}
                      title={canViewFile(f) ? 'View file' : undefined}
                    >
                      {f.status === 'uploading' ? (
                        <Loader2 className="h-5 w-5 text-slate-600 animate-spin" />
                      ) : (
                        <Paperclip className="h-5 w-5 text-slate-600" />
                      )}
                    </button>
                  )}

                  <div className="min-w-0">
                    {canViewFile(f) ? (
                      <button
                        type="button"
                        onClick={() => viewFile(f)}
                        className="text-sm font-semibold text-slate-900 truncate text-left hover:text-[#c00035] hover:underline focus:outline-none focus:underline"
                        title="View file"
                      >
                        {f.name}
                      </button>
                    ) : (
                      <div className="text-sm font-semibold text-slate-900 truncate">{f.name}</div>
                    )}
                    <div className="text-xs text-slate-500 mt-0.5">
                      {fmtSize(f.size)}
                      {f.status === 'uploaded' ? ' • uploaded' : f.status === 'uploading' ? ' • uploading…' : ''}
                      {f.status === 'error' ? ' • failed' : ''}
                    </div>
                    {f.error && (
                      <motion.div
                        initial={{ opacity: 0, y: -2 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-xs text-[#c00035] mt-1"
                      >
                        {f.error}
                      </motion.div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(f.id)}
                  className="p-1.5 rounded-full hover:bg-red-50 border border-transparent hover:border-red-100"
                  aria-label="Remove attachment"
                >
                  <X className="h-4 w-4 text-[#c00035]" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewFile?.url && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 !m-0 !p-0"
          onClick={closePreview}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] p-4"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePreview}
              className="absolute top-2 right-2 rounded-full bg-white p-2 shadow-lg hover:bg-slate-100"
              aria-label="Close preview"
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
            {isPreviewableImage(previewFile.type) ? (
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              />
            ) : isPdfFile(previewFile) ? (
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                className="w-[min(90vw,800px)] h-[85vh] rounded-lg shadow-2xl bg-white"
              />
            ) : (
              <video
                src={previewFile.url}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;

